// First-visit welcome popup. Mounted by AppShell while the session's
// enterprise has welcomeTourDone === false (the same flag the old CRM
// tour used); closing it marks the enterprise done, so it shows once per
// brokerage. When Testing mode is on — onboarding switches it on for new
// signups — it also explains that the dashboard is showing sample data.
import * as React from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, FlaskConical, Loader2, X } from 'lucide-react';
import { useAuth } from '@proptr/api-client/react';
import { isAgencyDataset, LISTING_IMAGES } from '../../lib/demoSource.js';
import { isTestMode, setTestMode } from '../../lib/testMode.js';
import { client } from '../../main.js';
import { Button } from '../ui/button.js';
import './welcome-modal.css';

export function WelcomeGate() {
  const { enterprise } = useAuth();
  const [dismissed, setDismissed] = React.useState(false);
  if (dismissed || !enterprise || enterprise.welcomeTourDone !== false) return null;
  return <WelcomeModal onDismissed={() => setDismissed(true)} />;
}

function WelcomeModal({ onDismissed }: { onDismissed: () => void }) {
  const { enterprise, refresh } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const testMode = isTestMode();
  const company = enterprise?.name || 'your brokerage';

  // Hide straight away; record it in the background. If the call fails the
  // popup simply comes back on the next visit.
  const finish = React.useCallback(() => {
    onDismissed();
    void client.enterprise
      .completeWelcomeTour()
      .then(() => refresh())
      .catch(() => {});
  }, [onDismissed, refresh]);

  const goLive = async () => {
    setBusy(true);
    try {
      await client.enterprise.completeWelcomeTour();
    } catch {
      // Not worth blocking the switch over.
    }
    setTestMode(false);
    window.location.reload();
  };

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && finish();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
    >
      <div className="wm-backdrop absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={finish} />
      <div className="wm-card relative max-h-full w-full max-w-[520px] overflow-y-auto rounded-2xl bg-card shadow-2xl">
        <WelcomeHero logo={enterprise?.logo ?? null} company={company} />
        <button
          type="button"
          onClick={finish}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-full bg-white/20 p-1.5 text-white backdrop-blur transition-colors hover:bg-white/30"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="space-y-5 px-6 pb-6 pt-5">
          <div>
            <div className="eyebrow">Welcome to Proptree</div>
            <h2
              id="welcome-title"
              className="mt-1 font-display text-[26px] font-medium leading-tight tracking-[-0.01em]"
            >
              You’re all set — let’s get started.
            </h2>
            <p className="mt-2 font-sans text-[14px] leading-relaxed text-muted-foreground">
              {company} is ready to go: your proptree, agents, listings and lead
              forms all live here.
            </p>
          </div>

          {testMode && (
            <div className="flex gap-3 rounded-xl border border-amber-300/70 bg-amber-50 p-4 text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-100">
              <FlaskConical className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <div className="space-y-1.5 font-sans text-[13px] leading-relaxed">
                <p className="font-semibold">You’re exploring in test mode</p>
                <p>
                  {isAgencyDataset
                    ? `The dashboard is filled with sample data built from ${company}’s listings and team, `
                    : 'The dashboard is filled with example data, '}
                  so you can click around freely. Nothing here is published, and no
                  leads or emails go out.
                </p>
                <p>
                  When you’re ready, press <strong>Exit</strong> on the yellow bar at
                  the top — or switch off <strong>Testing</strong> in your profile
                  menu — to see your live account.
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-2">
            {testMode && (
              <Button variant="outline" onClick={() => void goLive()} disabled={busy}>
                {busy && <Loader2 className="animate-spin" />}
                Switch to live data
              </Button>
            )}
            <Button onClick={finish} disabled={busy}>
              {testMode ? 'Start exploring' : 'Get started'}
              <ArrowRight />
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Decorative header: a mini proptree builds itself over drifting light,
// with the brokerage's own listing photos scrolling past.
function WelcomeHero({ logo, company }: { logo: string | null; company: string }) {
  const photos = LISTING_IMAGES.filter(Boolean).slice(0, 6);
  const strip = photos.length ? [...photos, ...photos] : [];
  return (
    <div className="wm-hero" aria-hidden>
      <span className="wm-orb wm-orb-a" />
      <span className="wm-orb wm-orb-b" />
      <span className="wm-orb wm-orb-c" />
      <span className="wm-spark" />
      <span className="wm-spark" />
      <span className="wm-spark" />
      <span className="wm-spark" />
      <span className="wm-spark" />

      <div className="wm-chip wm-chip-a">
        <span className="wm-chip-dot" style={{ background: '#22c55e' }} />
        New inquiry
      </div>
      <div className="wm-chip wm-chip-b">
        <span className="wm-chip-dot" style={{ background: '#6c5ce7' }} />
        1.2k page views
      </div>
      <div className="wm-chip wm-chip-c">
        <span className="wm-chip-dot" style={{ background: '#f59e0b' }} />
        Listings synced
      </div>

      <div className="wm-phone">
        <div className="wm-avatar">
          {logo ? <img src={logo} alt="" /> : company.charAt(0).toUpperCase()}
        </div>
        <div className="wm-line" style={{ width: '62%' }} />
        <div className="wm-line wm-line-sub" style={{ width: '42%' }} />
        <div className="wm-btn" />
        <div className="wm-btn" />
        <div className="wm-btn" />
        {strip.length > 0 && (
          <div className="wm-strip">
            <div className="wm-strip-track">
              {strip.map((src, i) => (
                <span key={i} className="wm-tile" style={{ backgroundImage: `url("${src}")` }} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
